/**
 * Propeller Travel Layout
 *
 * Layout wrapper for the /propeller (super-admin-facing) preview of the
 * passenger platform. Mirrors the styling of the pax shell (src/pax/) so
 * super admins see the same experience clients see.
 */

import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import {
  Search,
  Grid,
  Bell,
  Info,
  Menu,
  X,
  LogOut,
  Settings,
  ChevronDown,
  Clock,
  Terminal,
  Receipt,
  TrendingDown,
  UserCircle,
  Sparkles,
  ArrowLeft,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getPlatformSetting } from '../../services/permissionService';
import { getUserContext } from '../../services/unifiedPermissionService';
import { isStaffRole } from '../../utils/ssoPolicy';
import '../../styles/propellerTravel.css';

import propellerLogo from '../../assets/Propeller Travel Logo 448x100 copy.png';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  featureKey?: string;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/propeller/availability', label: 'Live Availability', icon: <Terminal className="w-4 h-4" /> },
  { path: '/propeller/fare-quote', label: 'Fare Quote', icon: <Receipt className="w-4 h-4" /> },
  { path: '/propeller/price-explorer', label: 'Price Explorer', icon: <TrendingDown className="w-4 h-4" /> },
  { path: '/propeller/seat-map', label: 'Seat Map', icon: <Grid className="w-4 h-4" /> },
  { path: '/propeller/search', label: 'Flight Search', icon: <Search className="w-4 h-4" />, featureKey: 'flight_search' },
  { path: '/propeller/seat-maps', label: 'Seat Maps (staff)', icon: <Grid className="w-4 h-4" />, featureKey: 'seat_maps' },
  { path: '/propeller/alerts', label: 'Alerts', icon: <Bell className="w-4 h-4" />, featureKey: 'alerts' },
  { path: '/propeller/flifo', label: 'FLIFO', icon: <Info className="w-4 h-4" />, featureKey: 'flifo_basic' },
  { path: '/propeller/mct', label: 'MCT', icon: <Clock className="w-4 h-4" />, featureKey: 'mct_checker' },
  { path: '/propeller/profile', label: 'Profile', icon: <UserCircle className="w-4 h-4" /> },
];

export function PropellerTravelLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { hasAccess, isApproved, loading } = usePermissions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [profileStaffRole, setProfileStaffRole] = useState(false);
  const [branding, setBranding] = useState({
    name: 'Propeller Travel',
    tagline: 'Your private flight intelligence',
  });

  useEffect(() => {
    let active = true;
    if (!user?.id) {
      setProfileStaffRole(false);
      return () => { active = false; };
    }
    getUserContext(user.id)
      .then((ctx) => {
        if (active) {
          setProfileStaffRole(Boolean(ctx?.isApproved && isStaffRole(ctx.role)));
        }
      })
      .catch(() => {
        if (active) setProfileStaffRole(false);
      });
    return () => {
      active = false;
    };
  }, [user?.id]);

  const isApprovedStaff = Boolean(isApproved && profileStaffRole);

  useEffect(() => {
    async function loadBranding() {
      try {
        const [name, tagline] = await Promise.all([
          getPlatformSetting<string>('brand_name'),
          getPlatformSetting<string>('brand_tagline'),
        ]);
        setBranding({
          name: name || 'Propeller Travel',
          tagline: tagline || 'Your private flight intelligence',
        });
      } catch (error) {
        console.error('Error loading branding:', error);
      }
    }
    loadBranding();
  }, []);

  const visibleNavItems = NAV_ITEMS.filter((item) => !item.featureKey || hasAccess(item.featureKey));

  if (!loading && !isApproved) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-8">
        <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl p-8 max-w-md text-center border border-slate-100 dark:border-slate-700">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Info className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
            Account Pending Approval
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            Your account is awaiting administrator approval. You'll receive an email once your account has been activated.
          </p>
          <button
            onClick={() => signOut()}
            className="w-full py-3 px-4 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors font-semibold"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  // Propeller brand palette: deep navy primary + gold accent (matches the
  // platform_settings defaults: primary_color #0F4C81, accent_color #D4AF37).
  const BRAND_PRIMARY = '#0F4C81';
  const BRAND_PRIMARY_DARK = '#0B3A66';
  const BRAND_ACCENT = '#D4AF37';

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 relative">
      {/* Brand-color header backdrop. Replaces the previous indigo gradient. */}
      <div
        className="absolute top-0 inset-x-0 h-[280px] overflow-hidden pointer-events-none"
        style={{ background: `linear-gradient(135deg, ${BRAND_PRIMARY} 0%, ${BRAND_PRIMARY_DARK} 100%)` }}
      >
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2074&auto=format&fit=crop')] opacity-10 bg-cover bg-center mix-blend-overlay" />
        <div className="absolute bottom-0 inset-x-0 h-px" style={{ background: BRAND_ACCENT, opacity: 0.6 }} />
      </div>

      {/* Top nav */}
      <nav className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 border-b border-white/10">
          <Link to="/propeller" className="flex items-center gap-3">
            {/* Logo on a brand-white pill so the colored mark always reads
                cleanly against the navy hero. The previous `brightness-0
                invert` filter rendered the logo as a flat white silhouette
                that lost the propeller graphic and the deep blue wordmark. */}
            <span className="inline-flex items-center bg-white/95 backdrop-blur rounded-2xl px-3 py-1.5 shadow-md">
              <img src={propellerLogo} alt={branding.name} className="h-7 sm:h-8 w-auto" />
            </span>
            <div className="hidden lg:flex items-center gap-3">
              <div className="h-6 w-px bg-white/30" />
              <span className="text-white/90 text-sm font-medium">{branding.tagline}</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {visibleNavItems.map((item) => {
              const isActive = location.pathname === item.path || (item.path !== '/propeller' && location.pathname.startsWith(item.path + '/'));
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </Link>
              );
            })}
          </div>

          {/* User chip */}
          <div className="flex items-center gap-2">
            {isApprovedStaff && (
              <Link
                to="/"
                data-testid="propeller-back-to-staff"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold bg-white/15 hover:bg-white/25 text-white border border-white/25 transition-colors"
                title="Return to the main staff platform"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Back to staff platform</span>
              </Link>
            )}
            <div className="relative">
              <button
                type="button"
                aria-label="Open user menu"
                aria-expanded={userMenuOpen}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-full text-white/85 hover:text-white hover:bg-white/10 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-medium backdrop-blur-sm border border-white/30 text-sm">
                  {user?.email?.charAt(0).toUpperCase() ?? 'P'}
                </div>
                <ChevronDown className="w-4 h-4 hidden sm:block" />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-60 bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden z-20">
                    <div className="p-3 border-b border-slate-200 dark:border-slate-700">
                      <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Signed in as</p>
                      <p className="font-medium text-slate-900 dark:text-slate-100 truncate text-sm mt-0.5">{user?.email}</p>
                    </div>
                    <div className="p-2">
                      {isApprovedStaff && (
                        <Link
                          to="/"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-slate-700 rounded-lg"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Back to staff platform
                        </Link>
                      )}
                      <Link
                        to="/propeller/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                      >
                        <UserCircle className="w-4 h-4" />
                        My profile
                      </Link>
                      <Link
                        to="/propeller/settings"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link>
                      <button
                        onClick={() => {
                          setUserMenuOpen(false);
                          signOut();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 rounded-lg"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            <button
              type="button"
              aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-white hover:bg-white/10 rounded-full"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-b border-white/10">
            <div className="py-3 space-y-1">
              {visibleNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                      isActive ? 'bg-white/20 text-white' : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                  >
                    {item.icon}
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Index hero — only on /propeller exactly */}
        {location.pathname === '/propeller' && (
          <div className="py-10">
            <div className="flex items-center gap-2 text-indigo-200 text-xs uppercase tracking-wider mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Super-admin preview
            </div>
            <h1 className="text-4xl font-bold text-white tracking-tight">Propeller Pax Platform</h1>
            <p className="text-indigo-100 text-lg mt-2 max-w-2xl">
              You're previewing the experience your clients see when they sign in to pax.propellertravel.com. Pick a tool to try it the way they would.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
              {visibleNavItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="text-left bg-white hover:bg-indigo-50 border border-indigo-100 rounded-2xl p-5 shadow-lg shadow-indigo-950/15 transition-all hover:-translate-y-0.5 hover:border-indigo-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-indigo-900"
                >
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-700 mb-3">
                    {item.icon}
                  </div>
                  <div className="text-slate-950 font-semibold">{item.label}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Main content */}
      <main className={`relative z-10 ${location.pathname === '/propeller' ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6'}`}>
        <Outlet />
      </main>
    </div>
  );
}

export default PropellerTravelLayout;
